import { Add, List } from "@mui/icons-material";
import { Alert, Button, Menu as MuiMenu, MenuItem, styled } from "@mui/material";
import type { TFunction } from "i18next";
import React, { type FC, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DataListRow } from "@/components/DataList/DataListRow";
import type { Document } from "@/declarations/models/Document";
import type { Menu } from "@/declarations/models/Menu";
import type { Schema } from "@/declarations/models/Schema";
import { useEditorStore } from "@/EditorContextProvider";
import CollapseBar from "@/framework/KioForm/common/CollapseBar";
import type { RelationFieldOptions } from "@/framework/KioForm/fields/DocumentRelationField";
import Loader from "@/framework/Loader";
import Api from "@/services/Api";
import Settings from "@/Settings";
import { cancellablePromise } from "@/utils/async";
import { filterNull, getValue } from "@/utils/obj";
import { useModal } from "@/framework/ModalManager/useModal";
import { useResolvePotentiallyLocalizedString } from "@/hooks/useResolvePotentiallyLocalizedString";
import { useModalManager } from "@/framework/ModalManager/store";
import { useModalId } from "@/framework/ModalManager/ModalIdProvider";
import { getSchemaName } from "@/utils/schema";
import { useFetchApplicationMenu } from "@/hooks/useFetchApplicationMenu";

interface DocumentSelectorProps {
  value?: number;
  onSelect: (document_id: number) => void;
  selectedLanguage: string;
  onTitleChange?: (t?: string | null) => void;
  multiple: boolean;
  selectedItems?: string[];
  uiOptions?: RelationFieldOptions;
  disableCreateButton?: boolean;
  enableNoEditAlert?: boolean;
}

function createDataListMapper(
  schemas: Schema[],
  getLocalizedString: (str: any) => any,
  appMenus: Menu[],
  t: TFunction
): (doc: Document) => DataListRow {
  const fieldsBySchemaId = schemas.reduce(
    (acc, v) =>
      acc.set(v.id!, {
        schemaName: v.name,
        description_field_override: v.description_field_override,
      }),
    new Map<
      number,
      Pick<
        Schema,
        | "internal_title_field_json_path"
        | "title_field_json_path"
        | "image_field_json_path"
        | "description_field_json_path"
        | "description_field_override"
      > & { schemaName: string }
    >()
  );

  return function mapper(doc: Document) {
    return {
      key: String(doc.id),
      title: doc.internal_title || getLocalizedString(doc.title) || "*",
      subTitle: getSchemaName(doc, getLocalizedString, appMenus),
      infoText: doc.status ? `${doc.id} - ${t(`status.${doc.status}`)}` : String(doc.id),
      chipContent: getValue(doc, "content.tags.internal"),
      imageURL: doc.media_data?.thumbnail_url || undefined,
      updatedAt: doc.updated_at,
      updatedBy: doc.updated_by,
    };
  };
}

const ButtonRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "row nowrap",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  width: "100%",
  "& .MuiButton-label": {
    textTransform: "none",
    fontWeight: 700,
  },
}));

const DocumentSelector: FC<DocumentSelectorProps> = ({
  onSelect,
  selectedLanguage = Settings.DEFAULT_LOCALE,
  onTitleChange,
  multiple,
  selectedItems,
  uiOptions,
  disableCreateButton = false,
  enableNoEditAlert = false,
}) => {
  const { t } = useTranslation("common");
  const { addDocumentIds } = useEditorStore();
  const [selectedSchemaId, setSelectedSchemaId] = useState<number | undefined>(undefined);

  const [document, setDocument] = useState<Document | null>(null);
  const [schemas, setSchemas] = useState<Array<Schema>>([]);
  const docRef = useRef<Document | null>(null);
  const [anchorElement, setAnchorElement] = useState<Element | null>(null);
  const getLocalizedString = useResolvePotentiallyLocalizedString(selectedLanguage);

  const addButtonLabel = uiOptions?.addButton?.["ui:label"] || t("components.relationField.findDocumentLabel");
  const createButtonLabel = uiOptions?.createButton?.["ui:label"] || t("components.relationField.createDocumentLabel");
  const schemaIds = useMemo(() => uiOptions?.schemaIds ?? [], [uiOptions?.schemaIds]);
  const appMenus = useFetchApplicationMenu();

  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { modalId } = useModalId();
  const contextDocumentId = useModalManager(
    (s) => s.editDocumentLoader?.modals?.[modalId ?? ""]?.props?.documentIdProp
  );
  const isContextDocumentDirty = useModalManager((s) => s.editDocumentLoader?.modals?.[modalId ?? ""]?.props?.isDirty);
  const modalCloser = useModalManager((s) => s.editDocumentLoader?.modals?.[modalId ?? ""]?.meta?.onClose);

  useEffect(() => {
    if (!schemaIds?.length) return;
    const [allSchemas, cancel] = cancellablePromise(Promise.all(schemaIds.map((id) => Api.getOneSchema(id).fetch())));
    allSchemas
      .then((schemasResponse) =>
        schemasResponse.map(([schema, error]) => {
          if (error) {
            console.error(error);
            return null;
          }
          return schema;
        })
      )
      .then((maybeNull) => maybeNull.filter(filterNull))
      .then(setSchemas);

    return cancel;
  }, [schemaIds, setSchemas]);

  useEffect(() => {
    if (!document?.id) return;
    onSelect(document.id);
    addDocumentIds([document.id]);
  }, [addDocumentIds, document?.id]);

  const mapper = useMemo(
    () => createDataListMapper(schemas, getLocalizedString, appMenus, t),
    [appMenus, getLocalizedString, schemas, t]
  );

  useEffect(() => {
    if (document) {
      const title = getLocalizedString(mapper(document).title);
      onTitleChange?.(title);
    } else {
      onTitleChange?.(null);
    }
    if (document === docRef.current) {
      return;
    }
  }, [mapper, onTitleChange, document, getLocalizedString]);

  const findModal = useModal(
    "documentListModalContent",
    useCallback(
      (close: () => void) => ({
        mapper,
        onSelect: (doc: Document) => {
          setDocument(doc);
          if (!multiple) close();
        },
        schemaIds,
        selectedItems,
        selectedLanguage,
      }),
      [mapper, multiple, schemaIds, selectedItems, selectedLanguage]
    ),
    useCallback(
      (close: () => void) => ({
        onClose: () => {
          // Moved isDirtyPrompt from here to DocumentListModalContentModal because of state being stale.
          close();
        },
      }),
      [isDirty, t]
    )
  );

  const createModal = useModal(
    "editDocumentLoader",
    {
      schemaId: selectedSchemaId,
      onSave: (doc: Document | undefined) => !!doc && setDocument(doc),
      disablePreview: false,
      isDirty,
      setIsDirty,
    },
    useCallback(
      (close: () => void) => ({
        onClose: () => {
          // Moved isDirtyPrompt from here to EditDocumentLoader because of state being stale.
          close();
        },
      }),
      [isDirty, t]
    )
  );

  const openCreateModal = (schemaId: number) => {
    setSelectedSchemaId(schemaId);
    createModal.open();
  };

  if (!schemas.length) return <Loader />;

  const mappedDoc = document ? mapper(document) : null;

  if (mappedDoc && typeof mappedDoc.title === "object") {
    if (Object.prototype.hasOwnProperty.call(mappedDoc.title, selectedLanguage)) {
      mappedDoc.title = mappedDoc.title[selectedLanguage];
    } else {
      mappedDoc.title = "";
    }
  }

  const schemaMenuOpen = Boolean(anchorElement);

  const openSchemaSelect = (e: MouseEvent<HTMLButtonElement>) => {
    if (schemas?.length === 1) {
      openCreateModal(schemas[0]!.id!);
    } else {
      setAnchorElement(e.currentTarget);
    }
  };
  const onOpenContextDocumentInNewWindow = (evt: React.MouseEvent) => {
    evt.preventDefault();
    if (!contextDocumentId) return;
    if (isContextDocumentDirty) {
      if (!window.confirm(t("generic.unsavedChangesMessage"))) {
        return;
      }
    }
    window.open(`${contextDocumentId}`, "_blank");
    !!modalCloser && modalCloser();
  };
  const onMenuClose = () => setAnchorElement(null);

  return (
    <>
      {!multiple && mappedDoc && (
        <CollapseBar title={String(mappedDoc.title)} onDelete={() => setDocument(null)} enableDelete disableExpansion />
      )}
      {(multiple || !document) && (
        <ButtonRow>
          <Button variant="outlined" color="primary" onClick={findModal.open} startIcon={<List />}>
            {addButtonLabel}
          </Button>
          <Button
            id="schema-menu-button"
            variant="outlined"
            color="primary"
            disabled={disableCreateButton}
            onClick={openSchemaSelect}
            startIcon={<Add />}
          >
            {createButtonLabel}
          </Button>
          <MuiMenu
            open={schemaMenuOpen}
            anchorEl={anchorElement}
            onClose={onMenuClose}
            MenuListProps={{
              "aria-labelledby": "schema-menu-button",
            }}
          >
            {schemas
              .filter((s) => s != null)
              .map((schema) => (
                <MenuItem
                  key={schema.id}
                  onClick={() => {
                    openCreateModal(schema!.id!);
                    setAnchorElement(null);
                  }}
                >
                  {schema?.name}
                </MenuItem>
              ))}
          </MuiMenu>
        </ButtonRow>
      )}
      {enableNoEditAlert && (
        <Alert
          sx={{
            margin: "10px 0 !important",
            padding: "8px 8px !important",
          }}
          severity="warning"
        >
          {t("documentSelector.openContextDocumentInNewWindowWarningPart1")}
          <a href={`${contextDocumentId}`} onClick={onOpenContextDocumentInNewWindow} target={"_blank"}>
            {t("documentSelector.linkText")}
          </a>
          {t("documentSelector.openContextDocumentInNewWindowWarningPart2")}
        </Alert>
      )}
    </>
  );
};

export default DocumentSelector;
